'use client'

import { useParams, usePathname } from 'next/navigation'
import { useCallback, useMemo } from 'react'

import type { AutocompleteChatTrail } from '@/components/chat/autocomplete-chat-trail'
import { resolveCurrentUrlParams } from '@/components/global-chat/active-surface'
import { useOptionalBrandContext } from '@/contexts/brand-context'
import { useUsageLimitsOptional } from '@/contexts/usage-limits-context'
import type {
  PromptSuggestionClientContext,
  PromptSuggestionSurface,
} from '@/lib/prompt-suggestions/context'
import { PROMPT_SUGGESTION_TITLE_MAX_CHARS } from '@/lib/prompt-suggestions/context'

export type PromptSuggestionContextResult = {
  context: PromptSuggestionClientContext
  /** Coarse cache-key prefix for inline autocomplete requests. */
  cacheKey: string
}

export type GetPromptSuggestionContext = () => PromptSuggestionContextResult

export type UsePromptSuggestionContextResult = {
  getContext: GetPromptSuggestionContext
  /** Changes only when semantic route/account context changes, never typing. */
  revisionKey: string
}

type UsePromptSuggestionContextOptions = {
  surface: PromptSuggestionSurface
  /** Lazily builds the conversation trail at fetch time (post-debounce).
   * Omit on surfaces without a conversation (launch composer). */
  getChatTrail?: () => AutocompleteChatTrail | null
}

/**
 * Assembles suggestion context from providers already mounted app-wide.
 * The conversation trail remains lazy so keystrokes do not rebuild it.
 */
export function usePromptSuggestionContext({
  surface,
  getChatTrail,
}: UsePromptSuggestionContextOptions): UsePromptSuggestionContextResult {
  const brandCtx = useOptionalBrandContext()
  const usage = useUsageLimitsOptional()
  // Deliberately NOT `useActiveSurface()` — it reads `useSearchParams()`,
  // which would force a Suspense boundary on statically-rendered public
  // pages that mount the launch composer. Pathname + route params carry
  // everything the suggestion context needs.
  const pathname = usePathname()
  const params = useParams()
  const currentUrlParams = resolveCurrentUrlParams(pathname, params)
  const path = pathname ? pathname.slice(0, 200) : undefined
  const automationId = currentUrlParams?.automationId?.slice(0, 64)
  const emailId = currentUrlParams?.emailId?.slice(0, 64)
  const brandId = brandCtx?.brandId || undefined
  const brandName = brandCtx?.brand?.brandName?.slice(
    0,
    PROMPT_SUGGESTION_TITLE_MAX_CHARS
  )
  const isUsageReady = Boolean(usage && !usage.isLoading)
  const plan = isUsageReady && usage ? usage.planKey : undefined
  let isCreditsLow: boolean | undefined
  if (isUsageReady && usage) {
    isCreditsLow = usage.remainingCredits <= 0 || usage.usagePercent >= 90
  }

  const getContext = useCallback(() => {
    const chatTrail = getChatTrail?.() ?? null
    const context: PromptSuggestionClientContext = {
      surface,
      path,
      automationId,
      emailId,
      brandId,
      brandName,
      plan,
      creditsLow: isCreditsLow,
      ...(chatTrail && chatTrail.trail.length > 0
        ? { chatTrail: chatTrail.trail }
        : {}),
      ...(chatTrail && chatTrail.artifacts.length > 0
        ? { artifacts: chatTrail.artifacts }
        : {}),
    }
    const cacheKey = JSON.stringify([
      surface,
      path ?? '',
      brandId ?? '',
      plan ?? '',
      chatTrail?.signature ?? '',
      isCreditsLow === undefined ? '' : Number(isCreditsLow),
    ])
    return { context, cacheKey }
  }, [
    automationId,
    brandId,
    brandName,
    isCreditsLow,
    emailId,
    getChatTrail,
    path,
    plan,
    surface,
  ])

  const revisionKey = JSON.stringify([
    surface,
    path ?? '',
    automationId ?? '',
    emailId ?? '',
    brandId ?? '',
    brandName ?? '',
    plan ?? '',
    isCreditsLow === undefined ? '' : Number(isCreditsLow),
  ])

  return useMemo(() => ({ getContext, revisionKey }), [getContext, revisionKey])
}
